import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import {
  Box,
  Button,
  Typography,
  Stack,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HomeIcon from '@mui/icons-material/Home'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import SquareFootIcon from '@mui/icons-material/SquareFoot'
import EnergyIcon from '@mui/icons-material/Bolt'
import PersonIcon from '@mui/icons-material/Person'
import TimelineIcon from '@mui/icons-material/Timeline'
import BarChartIcon from '@mui/icons-material/BarChart'

import TimelineCard from './TimelineCard'
import LikeDislikeButtons from './LikeDislikeButtons'
import NeighborhoodAnalyticsSection from './analytics/NeighborhoodAnalyticsSection'
import PropertyAnalyticsSection from './analytics/PropertyAnalyticsSection'
import ListingLocationMap from './ListingLocationMap'

const backendURL = process.env.REACT_APP_BACKEND_URL

export default function ListingManagementSection({
  listing,
  events,
  refreshData,
  user,
  userToken,
  session,
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [openError, setOpenError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [openAvailabilityDialog, setOpenAvailabilityDialog] = useState(false)
  const [userAvailability, setUserAvailability] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const notificationId = searchParams.get('notificationId')

  // ============================
  //    Fetch User Availability
  // ============================
  useEffect(() => {
    async function fetchUserAvailability() {
      if (!session || !session.user) return

      try {
        const { data, error } = await supabase
          .from('profile_availability')
          .select('*')
          .eq('profile_id', session.user.id)
          .single()

        if (error) {
          console.error('Error fetching user availability:', error)
          setUserAvailability(false)
          return
        }

        if (
          data !== null &&
          (Object.values(data.schedule).some((slots) => slots.length > 0) || data.is_flexible)
        ) {
          setUserAvailability(true)
        } else {
          setUserAvailability(false)
        }
      } catch (error) {
        console.error('Error fetching user availability:', error)
      }
    }

    fetchUserAvailability()
  }, [session])

  function userAvailabilitySet() {
    return userAvailability
  }

  // This callback will be triggered by the LikeDislikeButtons child after a successful update
  const handleLikeDislikeChange = async () => {
    refreshData()
  }

  // ============================
  //     Viewing & Offer Logic
  // ============================
  const userViewing = listing?.viewings?.find((v) => v.user_id === user?.id) || null
  const userOffer = listing?.offers?.find((o) => o.user_id === user?.id) || null

  // Viewing
  const canScheduleViewing = !userViewing
  const canCancelViewing = ['pending', 'requested', 'accepted', 'proposed', 'confirmed'].includes(
    userViewing?.status
  )
  const disableViewingButton =
    userViewing?.status === 'revoked' ||
    userViewing?.status === 'declined' ||
    userViewing?.status === 'canceled' ||
    userViewing?.status === 'completed' ||
    listing?.sold_at !== null

  const disableViewingButtonText = {
    revoked: t('listingManagement.viewingRevoked', 'Viewing Revoked'),
    declined: t('listingManagement.viewingDeclined', 'Viewing Declined'),
    completed: t('listingManagement.viewingCompleted', 'Viewing Completed'),
    canceled: t('listingManagement.viewingCanceled', 'Viewing Canceled'),
  }

  // Offer
  const canMakeOffer = !userOffer
  const canRevokeOffer = userOffer?.status === 'pending' || userOffer?.status === 'accepted'
  const disableOfferButton =
    userOffer?.status === 'revoked' || userOffer?.status === 'declined' || listing?.sold_at !== null

  // ============================
  //     Schedule Viewing
  // ============================
  async function scheduleViewingInternal() {
    try {
      const response = await fetch(`${backendURL}/viewings/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          listing_id: listing.id,
        }),
      })

      if (!response.ok && response.status === 424) {
        const errorData = await response.json()
        console.error('Error scheduling viewing:', errorData)

        if (errorData.message === 'profile not complete') {
          setErrorMessage(
            t(
              'listingManagement.error.profileNotComplete',
              'Your profile is not complete. Please complete your profile before scheduling a viewing.'
            )
          )
        } else if (errorData.message === 'documents are missing') {
          setErrorMessage(
            t(
              'listingManagement.error.documentsMissing',
              'Your required documents are missing. Please upload all required documents before scheduling a viewing.'
            )
          )
        } else {
          setErrorMessage(
            t(
              'listingManagement.error.profileIncompleteOrDocumentsMissing',
              'Your profile is incomplete or required documents are missing. Please complete your profile.'
            )
          )
        }
        setOpenError(true)
        return
      }

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error scheduling viewing:', errorData)
        return
      }

      refreshData()
    } catch (error) {
      console.error('Error scheduling viewing:', error)
    }
  }

  async function handleScheduleViewing() {
    if (!canScheduleViewing) return

    if (!userAvailabilitySet()) {
      setOpenAvailabilityDialog(true)
      return
    }

    scheduleViewingInternal()
  }

  // ============================
  // Confirm Viewing
  // ============================
  async function handleConfirmViewing() {
    if (!userViewing || userViewing.status !== 'proposed') return

    try {
      const response = await fetch(`${backendURL}/viewings/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          viewing_id: userViewing.id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error confirming viewing:', errorData)
        return
      }

      refreshData()
    } catch (error) {
      console.error('Error confirming viewing:', error)
    }
  }

  // ============================
  // Cancel Viewing
  // ============================
  async function handleCancelViewing(viewingId) {
    if (!viewingId || !canCancelViewing) return

    try {
      const response = await fetch(`${backendURL}/viewings/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          viewing_id: viewingId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error canceling viewing:', errorData)
        return
      }

      refreshData()
    } catch (error) {
      console.error('Error canceling viewing:', error)
    }
  }

  // ============================
  //     Make / Revoke Offer
  // ============================
  async function handleMakeOffer() {
    if (!canMakeOffer) return

    try {
      const response = await fetch(`${backendURL}/offers/make`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          listing_id: listing.id,
          offer_price: listing.price,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error making offer:', errorData)
        return
      }

      refreshData()
    } catch (error) {
      console.error('Error making offer:', error)
    }
  }

  async function handleRevokeOffer(offerId) {
    if (!offerId || !canRevokeOffer) return

    try {
      const response = await fetch(`${backendURL}/offers/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          offer_id: offerId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error revoking offer:', errorData)
        return
      }

      refreshData()
    } catch (error) {
      console.error('Error revoking offer:', error)
    }
  }

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Back Button */}
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
        onClick={() => navigate(notificationId ? `/notifications/${notificationId}` : -1)}
      >
        {t('listingManagement.back', 'Back')}
      </Button>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Property Info and Main Actions */}
        <Grid item xs={12} md={8}>
          {/* Property Hero Card */}
          <Card 
            elevation={2} 
            sx={{ 
              mb: 3, 
              background: 'linear-gradient(to right, #f8f9fa, #e9ecef)',
              position: 'relative',
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              {/* Header with Title and Like/Dislike */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                mb: 2 
              }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                      {listing.name}
                    </Typography>
                    <LikeDislikeButtons 
                      listingId={listing.id} 
                      onLikeDislikeChange={handleLikeDislikeChange} 
                    />
                    {listing.sold_at && (
                      <Chip 
                        label={t('listingManagement.sold', 'SOLD')} 
                        color="error" 
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    )}
                  </Box>
                  <Typography variant="h5" sx={{ mt: 1, color: 'primary.main', fontWeight: '500' }}>
                    ${listing.price?.toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Property Info Grid */}
              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid item xs={6} md={3}>
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {t('listingManagement.city', 'City')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {listing?.city || t('listingManagement.notAvailable', 'N/A')}
                    </Typography>
                  </Stack>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HomeIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {t('listingManagement.postcode', 'Postcode')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {listing?.postcode || t('listingManagement.notAvailable', 'N/A')}
                    </Typography>
                  </Stack>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EnergyIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {t('listingManagement.energyLabel', 'Energy Label')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {listing?.energy_label || t('listingManagement.notAvailable', 'N/A')}
                    </Typography>
                  </Stack>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SquareFootIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {t('listingManagement.surfaceArea', 'Surface Area')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {listing?.surface
                        ? `${listing.surface} m²`
                        : t('listingManagement.notAvailable', 'N/A')}
                    </Typography>
                  </Stack>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {t('listingManagement.makelaar', 'Makelaar')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {listing.makelaar_name || t('listingManagement.notAvailable', 'N/A')}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                sx={{ mt: 2 }}
                justifyContent="flex-start"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                {/* Viewing Button(s) */}
                {userViewing?.status === 'proposed' ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    gap: 2,
                    width: '100%'
                  }}>
                    <Typography variant="body1" sx={{ alignSelf: 'center' }}>
                      {t('listingManagement.proposed', 'Proposed')}: {new Date(userViewing.viewing_date).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="success" 
                        onClick={handleConfirmViewing}
                        fullWidth
                      >
                        {t('listingManagement.accept', 'Accept')}
                      </Button>
                      <Button 
                        variant="contained" 
                        color="error" 
                        onClick={() => handleCancelViewing(userViewing.id)}
                        fullWidth
                      >
                        {t('listingManagement.cancel', 'Cancel')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    onClick={
                      canCancelViewing
                        ? () => handleCancelViewing(userViewing?.id)
                        : handleScheduleViewing
                    }
                    disabled={disableViewingButton}
                    color={canCancelViewing ? 'error' : 'primary'}
                    fullWidth={isMobile}
                    sx={{ flex: { xs: '1', sm: '0 0 auto' } }}
                  >
                    {canCancelViewing
                      ? t('listingManagement.cancelViewing', 'Cancel Viewing')
                      : disableViewingButton || userViewing?.status === 'canceled'
                      ? disableViewingButtonText[userViewing?.status]
                      : t('listingManagement.scheduleViewing', 'Schedule Viewing')}
                  </Button>
                )}

                {/* Offer Button */}
                <Button
                  variant="contained"
                  onClick={canRevokeOffer ? () => handleRevokeOffer(userOffer?.id) : handleMakeOffer}
                  disabled={disableOfferButton}
                  color={canRevokeOffer ? 'error' : 'primary'}
                  fullWidth={isMobile}
                  sx={{ flex: { xs: '1', sm: '0 0 auto' } }}
                >
                  {canRevokeOffer
                    ? t('listingManagement.revokeOffer', 'Revoke Offer')
                    : disableOfferButton
                    ? t('listingManagement.offerRevoked', 'Offer Revoked')
                    : t('listingManagement.makeOffer', 'Make Offer')}
                </Button>

                <Button
                  variant="outlined"
                  component="a"
                  href={listing.listing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth={isMobile}
                  sx={{ flex: { xs: '1', sm: '0 0 auto' } }}
                >
                  {t('listingManagement.visitListing', 'Visit Listing')}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Property Analytics Section - No Collapse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <PropertyAnalyticsSection listing={listing} userToken={userToken} />
            </CardContent>
          </Card>

          {/* Neighborhood Analytics Section - No Collapse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <NeighborhoodAnalyticsSection listing={listing} userToken={userToken} />
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Map and Timeline */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Map Card - Now first to prevent overlap issue */}
            <Card elevation={2} sx={{
              position: 'relative',
              zIndex: 1 // Ensures map is above other elements
            }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon color="primary" />
                    <Typography variant="h6">
                      {t('listingManagement.location', 'Location')}
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box sx={{ height: '400px', width: '100%' }}>
                  <ListingLocationMap
                    locationName={listing.name}
                    lat={listing.latitude}
                    lon={listing.longitude}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Timeline Card */}
            <Card elevation={2} sx={{ 
              position: 'relative',
              zIndex: 0 // Ensures timeline is below map
            }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon color="primary" />
                    <Typography variant="h6">
                      {t('listingManagement.timeline', 'Property Timeline')}
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <TimelineCard events={events} />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Snackbar for errors */}
      <Snackbar
        open={openError}
        autoHideDuration={6000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenError(false)} severity="warning" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Availability Dialog */}
      <Dialog open={openAvailabilityDialog} onClose={() => setOpenAvailabilityDialog(false)}>
        <DialogTitle>
          {t('listingManagement.availabilityTitle', 'Set Your Availability')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'listingManagement.availabilityMessage',
              "It looks like you haven't set your availability yet. You can "
            )}
            <Link href="/user?tab=availability" underline="hover">
              {t('listingManagement.setAvailabilityLink', 'update your availability')}
            </Link>
            {t(
              'listingManagement.availabilityMessageCont',
              ' here, or proceed with scheduling using your current available times.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigate('/user?tab=availability')
            }}
            color="primary"
          >
            {t('listingManagement.setAvailability', 'Set Availability')}
          </Button>
          <Button
            onClick={() => {
              setOpenAvailabilityDialog(false)
              scheduleViewingInternal()
            }}
            color="primary"
            autoFocus
          >
            {t('listingManagement.proceedAnyway', 'Proceed Anyway')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}